import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Star, ArrowLeft, Clock, BookOpen, Award, MessageSquare, Info } from 'lucide-react';
import { toast } from 'sonner';
import BookingTimeline from '../components/BookingTimeline';
import GroupOptions from '../components/booking/GroupOptions';
import PricingTiers from '../components/booking/PricingTiers';

const TeacherDetail = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [subject, setSubject] = useState('');
  const [section, setSection] = useState('');
  const [chapter, setChapter] = useState('');
  const [slot, setSlot] = useState(null); // {date, start_time, end_time, duration_hours}
  const [groupSize, setGroupSize] = useState(1);
  const [visibility, setVisibility] = useState('invite_only');

  useEffect(() => {
    fetchTeacherData();
  }, [teacherId]);

  const fetchTeacherData = async () => {
    try {
      const [teacherRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/teachers/${teacherId}`),
        axios.get(`${API}/teachers/${teacherId}/ratings`)
      ]);
      setTeacher(teacherRes.data);
      setRatings(ratingsRes.data);
    } catch (error) {
      toast.error('Failed to load teacher details');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = () => {
    if (!subject) {
      toast.error('Please select a subject');
      return;
    }
    if (!slot) {
      toast.error('Please select a time slot from the timeline');
      return;
    }

    const bookingInfo = {
      subject,
      section,
      chapter,
      booking_date: slot.date,
      start_time: slot.start_time,
      duration_hours: slot.duration_hours,
      teacher_id: teacherId,
      teacher_name: teacher.name,
      group_size: groupSize,
      visibility: groupSize > 1 ? visibility : 'invite_only',
      rate_per_hour: rate,
      total_amount: total
    };

    navigate('/payment', { state: { bookingInfo } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Teacher not found</div>
      </div>
    );
  }

  const tiers = teacher.pricing_tiers?.length ? teacher.pricing_tiers : [{ students: 1, rate_per_hour: teacher.rate_per_hour }];
  const rate = (tiers.find((t) => t.students === groupSize) || tiers[0]).rate_per_hour;
  const total = Math.round(rate * (slot?.duration_hours || 0) * 100) / 100;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="teacher-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate('/teachers')}
        className="mb-6"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Teachers
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Teacher Profile */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-12 h-12 text-violet-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-3xl mb-2">{teacher.name}</CardTitle>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={`teacher-star-${i}`}
                          className={`w-5 h-5 ${
                            i < Math.floor(teacher.rating)
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-lg font-semibold">{teacher.rating.toFixed(1)}</span>
                    <span className="text-slate-500">({teacher.total_ratings} reviews)</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {teacher.subjects.map(subject => (
                      <Badge key={subject} className="bg-violet-100 text-violet-700 border-violet-300">
                        {subject}
                      </Badge>
                    ))}
                  </div>

                  {teacher.languages_spoken?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {teacher.languages_spoken.map(language => (
                        <Badge key={language} className="bg-emerald-100 text-emerald-700 border-emerald-300">
                          {language}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <Award className="w-5 h-5 text-violet-600" />
                  Experience & Qualifications
                </h3>
                <p className="text-slate-600 mb-2">{teacher.experience_years} years of teaching experience</p>
                <p className="text-slate-600">{teacher.qualifications}</p>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">About</h3>
                <p className="text-slate-600 leading-relaxed">{teacher.bio}</p>
              </div>

              <PricingTiers tiers={tiers} />
            </CardContent>
          </Card>

          {/* Ratings */}
          {ratings.length > 0 && (
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  Student Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ratings.slice(0, 5).map((rating, index) => (
                    <div key={rating.id} className="pb-4 border-b border-slate-200 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={`${rating.id}-star-${i}`}
                            className={`w-4 h-4 ${
                              i < rating.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'
                            }`}
                          />
                        ))}
                        <span className="text-sm text-slate-500">
                          {new Date(rating.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {rating.review && <p className="text-slate-600">{rating.review}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Booking Form */}
        <div className="lg:col-span-1">
          <Card className="border-0 shadow-xl sticky top-24">
            <CardHeader>
              <CardTitle>Book Session</CardTitle>
              <CardDescription>Choose your subject and pick a time on the timeline</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger data-testid="booking-subject">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {teacher.subjects.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Section (Optional)</Label>
                <Input
                  placeholder="e.g., Mechanics"
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  data-testid="booking-section"
                />
              </div>

              <div className="space-y-2">
                <Label>Chapter (Optional)</Label>
                <Input
                  placeholder="e.g., Newton's Laws"
                  value={chapter}
                  onChange={(e) => setChapter(e.target.value)}
                  data-testid="booking-chapter"
                />
              </div>

              <div className="border-t border-slate-200 pt-4">
                <BookingTimeline teacherId={teacherId} onSelectionChange={setSlot} />
              </div>

              <div className="border-t border-slate-200 pt-4">
                <GroupOptions
                  tiers={tiers}
                  groupSize={groupSize}
                  onGroupSize={setGroupSize}
                  visibility={visibility}
                  onVisibility={setVisibility}
                />
              </div>

              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="cancellation-policy-note">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  You can cancel the booking a minimum of 1 hour before the session time by using the
                  {' '}<span className="font-semibold">"Talk to Executive"</span> option.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Rate ({groupSize} {groupSize === 1 ? 'student' : 'students'}):</span>
                  <span className="font-semibold" data-testid="booking-rate">₹{rate}/hr</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Duration:</span>
                  <span className="font-semibold">{slot ? `${slot.duration_hours * 60} mins` : '—'}</span>
                </div>
                <div className="border-t border-slate-300 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-lg">Total:</span>
                    <span className="font-bold text-2xl text-violet-600" data-testid="booking-total">
                      ₹{total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleBooking}
                disabled={!slot || !subject}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 font-medium shadow-lg shadow-violet-500/25 disabled:opacity-50"
                data-testid="proceed-payment-button"
              >
                Proceed to Payment
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TeacherDetail;